// text property of node is not updated through any form

import type { Node } from 'reactflow';

import { DEFAULT_PARAMS } from '@/data/default-node-params';
import { nodeTypes } from '@/data/node-type-options';
import {
    BotModuleData,
    CalendarModuleBotModuleData,
    CustomInternalModuleBotModuleData,
    ExitModuleData,
    MDataChatModuleBotModuleData,
    ModuleSource,
    MSearchModuleBotModuleData,
    NumericRatingBotModuleData,
    OpenQuestionBotModuleData,
    PromptBotModuleData,
    RestCalloutData,
    SMSModuleBotModuleData,
    StickersBotModuleData,
    TransferToAgentBotModuleData,
    ValueRangeRouterBotModuleData,
    YesNoConfirmationBotModuleData,
} from '@/domain/bot';
import { PhonePrefixRegex } from '@/schemas/phone-validation-schema';

function parseCustomPhoneNumber(number?: string) {
    if (!number) {
        return null;
    }

    const match = number.match(PhonePrefixRegex);

    if (match?.index === 0) {
        return {
            isCustomPhone: true,
            phonePrefix: match[0],
            phoneNumber: number.substring(match[0].length),
        };
    }

    return null;
}

/**
 * Creates node in flow graph
 * @param id - unique id of node
 * @param type - one of BotMappieModulePresetType or string naming any CustomInternalModule
 * @param params - node.data params
 * @param source - node.data source
 * @returns object representing node in flow graph
 */

export const createNodeReactFlow = (
    id: string,
    type: any,
    params?: { [key: string]: string | string[] | number },
    source?: ModuleSource,
    xPosition?: number,
    yPosition?: number,
): Node<BotModuleData> => {
    let unknownTypeForCustomModule;
    if (id.includes('custom_internal_module') && !nodeTypes.includes(type)) {
        unknownTypeForCustomModule = type;
    }

    const basicNode = {
        id: id,
        type: `${
            unknownTypeForCustomModule ? 'CustomInternalModule' : type
        }Node`,
        position: { x: xPosition ?? 0, y: yPosition ?? 0 },
    };

    const basicData = {
        type: type,
        valid: true,
    };

    switch (type) {
        case 'Prompt':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    params: params ?? DEFAULT_PARAMS.Prompt,
                } as PromptBotModuleData,
            };

        case 'OpenQuestion':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    params: params ?? DEFAULT_PARAMS.OpenQuestion,
                } as OpenQuestionBotModuleData,
            };

        case 'SMSModule':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    params: params
                        ? {
                              ...params,
                              ...parseCustomPhoneNumber(
                                  params.target_sms_number as string,
                              ),
                              target_sms_number: undefined,
                          }
                        : DEFAULT_PARAMS.SMSModule,
                } as SMSModuleBotModuleData,
            };

        case 'YesNoConfirmation':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    params: params
                        ? {
                              ...params,
                              escape_mode: String(params?.escape_mode),
                          }
                        : DEFAULT_PARAMS.YesNoConfirmation,
                } as YesNoConfirmationBotModuleData,
            };

        case 'GPTModule':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    params: params
                        ? {
                              ...params,
                          }
                        : DEFAULT_PARAMS.GPTModule,
                } as YesNoConfirmationBotModuleData,
            };

        case 'NumericRatingModule':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    params: params ?? DEFAULT_PARAMS.NumericRatingModule,
                } as NumericRatingBotModuleData,
            };

        case 'ValueRangeRouter':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    params: params ?? {
                        value: '',
                        min_value: undefined,
                        max_value: undefined,
                        interval_partition_points: [],
                    },
                } as ValueRangeRouterBotModuleData,
            };

        case 'StickerModule':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    params: params ?? DEFAULT_PARAMS.StickerModule,
                } as StickersBotModuleData,
            };

        case 'CalendarModule':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    source: DEFAULT_PARAMS.CalendarModule,
                } as CalendarModuleBotModuleData,
            };

        case 'CustomInternalModule':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    typeOverride: type ?? '',
                    params: params ?? {},
                    source: source ?? {
                        path: '',
                        unpack_params: true,
                    },
                } as CustomInternalModuleBotModuleData,
            };

        case 'MSearchModule':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    params: params ?? DEFAULT_PARAMS.MSearchModule,
                } as MSearchModuleBotModuleData,
            };

        case 'MDataChatModule':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    params: params ?? DEFAULT_PARAMS.MDataChatModule,
                } as MDataChatModuleBotModuleData,
            };

        case 'RestCallout':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    params: params ?? DEFAULT_PARAMS.RestCallout,
                } as RestCalloutData,
            };

        case 'TransferToAgentModule':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                    params: params ?? DEFAULT_PARAMS.TransferToAgentModule,
                } as TransferToAgentBotModuleData,
            };

        case 'Exit':
            return {
                ...basicNode,
                data: {
                    ...basicData,
                } as ExitModuleData,
            };

        default:
            return {
                id: id,
                type: 'CustomInternalModuleNode',
                data: {
                    type: 'CustomInternalModule',
                    typeOverride: type,
                    valid: true,
                    params: params ?? {},
                    source: source ?? {
                        path: '',
                        unpack_params: true,
                    },
                },
                position: unknownTypeForCustomModule
                    ? basicNode.position
                    : { x: 0, y: 0 },
            };
    }
};
